import { useCallback, useMemo } from 'react';

import { IUseQueryProps } from 'store/queries/types';
import { getQueryParams } from 'store/utils/getQueryParams';
import { useAppSelector } from 'store/useAppSelector';

import {
  IFetchAccountStatusParams,
  selectAccountStatusDataWithFallback,
  selectAccountStatusLoading,
  selectAccountStatusState,
  useFetchAccountStatusQuery,
  useLazyFetchAccountStatusQuery,
} from '../fetchAccountStatus';

export interface IUseFetchAccountStatusQueryProps
  extends IUseQueryProps,
    IFetchAccountStatusParams {}

export const useAccountStatusQuery = ({
  pollingInterval,
  skipFetching,
  userEndpointToken,
}: IUseFetchAccountStatusQueryProps) => {
  const params = useMemo(
    (): IFetchAccountStatusParams => ({ userEndpointToken }),
    [userEndpointToken],
  );

  useFetchAccountStatusQuery(getQueryParams({ params, skipFetching }), {
    pollingInterval,
    refetchOnMountOrArgChange: true,
  });

  const [fetchLazy] = useLazyFetchAccountStatusQuery();

  const handleFetchAccountStatus = useCallback(
    () => fetchLazy(params),
    [fetchLazy, params],
  );

  const status = useAppSelector(state =>
    selectAccountStatusDataWithFallback(state, params),
  );

  const loading = useAppSelector(state =>
    selectAccountStatusLoading(state, params),
  );

  const state = useAppSelector(storeState =>
    selectAccountStatusState(storeState, params),
  );

  return { handleFetchAccountStatus, loading, params, state, status };
};
